import { type AgeRange, AgeRangeMap, AgeRangeOptions, Gender, GenderMap } from "@qendo/types";
import { useEffect, useId, useState } from "react";
import { Button } from "~/components/Button";
import { AvatarPicker } from "~/components/form/auth/AvatarPicker";
import { ComboBox } from "~/components/form/combo-box/ComboBox";
import { CountrySelector } from "~/components/form/combo-box/CountrySelector";
import { FieldGroup } from "~/components/form/FieldGroup";
import type { FileValue } from "~/components/form/gallery/ImagePickerModal";
import { TextInput } from "~/components/form/TextInput";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";

type ProfileFormProps = {
    onRequestClose: () => void;
} & FullPageModalProps;

export const ProfileForm = ({ onRequestClose, ...modalProps }: ProfileFormProps) => {
    const { profile, can_update, updateProfile } = useProfile();
    const [photograph, setPhotograph] = useState<FileValue<false, true>>(profile?.photograph_id ?? null);
    const [name, setName] = useState<string>(profile?.name ?? "");
    const [phone, setPhone] = useState<string>(profile?.phone ?? "");
    const [ageRange, setAgeRange] = useState<AgeRange | null>(profile?.age_range ?? null);
    const [gender, setGender] = useState<Gender | null>(profile?.gender ?? null);
    const [postcode, setPostcode] = useState<string>(profile?.postcode ?? "");
    const [country, setCountry] = useState<string | null>(profile?.country ?? null);
    const { addToast, saving, setSaving } = useFeedback();
    const id = useId();

    const canSave = can_update && !saving;

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        const payload = {
            name,
            phone,
            age_range: ageRange,
            gender,
            postcode,
            country,
            photograph,
        };

        updateProfile(payload)
            .then(() => {
                addToast({
                    icon: "checkmark-circle",
                    content: "Profile updated",
                    duration: 3000,
                });

                onRequestClose();
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            title="Add account details"
            cancelable
            headerRight={() => (
                <Button title="Save" size="sm" disabled={!canSave} onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={onRequestClose}
        >
            <FieldGroup>
                <AvatarPicker photograph={photograph} onAvatarSelected={setPhotograph} disabled={is_saving} />
                <TextInput label="Name" value={name} onChangeText={setName} autoComplete="name" />
                <TextInput
                    label="Contact number"
                    value={phone}
                    onChangeText={setPhone}
                    autoComplete="tel"
                    inputMode="tel"
                />
                <ComboBox<AgeRange>
                    items={AgeRangeOptions}
                    itemMap={AgeRangeMap}
                    value={ageRange}
                    onItemSelected={setAgeRange}
                    label="Age range"
                    disabled={is_saving}
                    zIndex={2300}
                />
                <ComboBox<Gender>
                    items={Object.values(Gender)}
                    itemMap={GenderMap}
                    value={gender}
                    onItemSelected={setGender}
                    label="Gender"
                    disabled={is_saving}
                    zIndex={2200}
                />
                <TextInput label="Postcode" value={postcode} onChangeText={setPostcode} />
                <CountrySelector value={country} onItemSelected={setCountry} zIndex={2100} />
            </FieldGroup>
        </FullPageModal>
    );
};
